import React, { useState } from 'react';
import { FileCode, X, Play, Loader, Copy, Check } from 'lucide-react';
import { useClaimStore } from '../store/claimStore';
import { testClaimMapper } from '../services/api/endpoints/claimEndpoints';
import Button from './Button';
import Modal from './Modal';
import { logger } from '../utils/logger';

/**
 * Dev-only button to test Portal -> Nova mapping
 * Only shown in development mode (import.meta.env.MODE === 'development')
 */
const TestMapperButton: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eventId, setEventId] = useState('');
  const [caseId, setCaseId] = useState('');
  const [claimDataJson, setClaimDataJson] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { currentClaim, stepData } = useClaimStore();

  // Only render in development mode OR when running on dev/localhost URLs
  const isDevelopment = import.meta.env.MODE === 'development' ||
                        window.location.hostname === 'localhost' ||
                        window.location.hostname.includes('dev') ||
                        window.location.hostname.includes('127.0.0.1');

  if (!isDevelopment) {
    return null;
  }

  const handleTest = async () => {
    if (!eventId.trim() || !caseId.trim()) {
      setError('Both Event ID and Case ID are required');
      return;
    }

    if (!claimDataJson.trim()) {
      setError('Portal claim data (JSON) is required');
      return;
    }

    setIsTesting(true);
    setError(null);
    setResult(null);

    try {
      // Parse the pasted JSON
      let claimData: Record<string, any>;
      try {
        claimData = JSON.parse(claimDataJson);
      } catch (parseErr) {
        setError('Invalid JSON format in claim data field');
        setIsTesting(false);
        return;
      }

      const response = await testClaimMapper(eventId, caseId, claimData);
      setResult(response);
    } catch (err: any) {
      setError(err.message || 'Failed to test mapper');
      logger.error('Test mapper error', {
        errorType: 'TestMapperError',
        component: 'TestMapperButton'
      }, err);
    } finally {
      setIsTesting(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setResult(null);
    setError(null);
  };

  // Pre-fill fields from current claim if available
  React.useEffect(() => {
    if (currentClaim?.eventId && !eventId) {
      setEventId(currentClaim.eventId);
    }
    // caseId is no longer carried on the claim response (DEV-7027) — the dev still
    // types it manually into the form for the testMapper endpoint.
    // Pre-fill claim data JSON from current claim with proper Portal_Data__c structure
    if ((currentClaim || stepData) && !claimDataJson) {
      const claimData = {
        workflow: {
          currentScreenCode: currentClaim?.currentScreenCode || '04-03-JUSTIFICATIFS',
          stepData: stepData || {},
        },
      };
      setClaimDataJson(JSON.stringify(claimData, null, 2));
    }
  }, [currentClaim, stepData, eventId, caseId, claimDataJson]);

  const handleCopyToClipboard = async () => {
    if (!result) return;

    const textToCopy = JSON.stringify(result, null, 2);

    try {
      // Try modern Clipboard API first
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback: Create a temporary textarea element
      try {
        const textarea = document.createElement('textarea');
        textarea.value = textToCopy;
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        textarea.style.left = '-9999px';
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);

        // Select the text
        textarea.select();
        textarea.setSelectionRange(0, textToCopy.length);

        // Copy using execCommand
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else {
          logger.error('execCommand copy failed', {
            errorType: 'ClipboardError',
            component: 'TestMapperButton'
          });
          alert('Failed to copy. Please manually select and copy the JSON.');
        }
      } catch (fallbackErr) {
        logger.error('Fallback copy failed', {
          errorType: 'ClipboardFallbackError',
          component: 'TestMapperButton'
        }, fallbackErr as Error);
        alert('Copy failed. Please manually select and copy the JSON.');
      }
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsModalOpen(true)}
        className="fixed bottom-6 right-6 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110 z-50"
        title="Test Portal → Nova Mapper (Dev Only)"
      >
        <FileCode className="w-6 h-6" />
      </button>

      {/* Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleClose}
          title="Test Portal → Nova Mapper"
          size="lg"
        >
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Dev Tool:</strong> Test the mapping of current claim data from Portal format to Nova format.
                This calls the <code className="bg-blue-100 px-1 rounded">POST /api/v1/claims/testMapper/:eventId/:caseId</code> endpoint.
              </p>
            </div>

            {/* Input Fields */}
            <div className="space-y-3">
              <div>
                <label htmlFor="eventId" className="block text-sm font-medium text-gray-700 mb-1">
                  Event ID (Salesforce Event__c) <span className="text-red-500">*</span>
                </label>
                <input
                  id="eventId"
                  type="text"
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  placeholder="e.g., a0X1234567890ABC"
                  className="form-input w-full"
                  disabled={isTesting}
                />
              </div>

              <div>
                <label htmlFor="caseId" className="block text-sm font-medium text-gray-700 mb-1">
                  Case ID (Salesforce Case) <span className="text-red-500">*</span>
                </label>
                <input
                  id="caseId"
                  type="text"
                  value={caseId}
                  onChange={(e) => setCaseId(e.target.value)}
                  placeholder="e.g., 5001234567890ABC"
                  className="form-input w-full"
                  disabled={isTesting}
                />
              </div>

              <div>
                <label htmlFor="claimData" className="block text-sm font-medium text-gray-700 mb-1">
                  Portal Claim Data (JSON) <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="claimData"
                  value={claimDataJson}
                  onChange={(e) => setClaimDataJson(e.target.value)}
                  placeholder='{"workflow":{"currentScreenCode":"04-03-JUSTIFICATIFS","stepData":{"01-00-DEMANDEUR":{"firstName":"Jean-Pierre","lastName":"Tremblay"}}}}'
                  className="form-input w-full font-mono text-xs"
                  rows={12}
                  disabled={isTesting}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste the Portal_Data__c JSON payload here. Structure: <code className="bg-gray-100 px-1 rounded">{"{"}"workflow":{"{"}"currentScreenCode":"...","stepData":{"{"}...{"}"}{"}"}{"}"}</code>
                </p>
              </div>
            </div>

            {/* Current Claim Info */}
            {currentClaim && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">Current Claim Data:</p>
                <p className="text-xs text-gray-600">
                  Event: {currentClaim.eventId || 'N/A'} |
                  Event #: {currentClaim.eventNumber || 'N/A'} |
                  Screens filled: {Object.keys(stepData || {}).length}
                </p>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Result Display */}
            {result && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-h-96 overflow-auto">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-green-800">Nova JSON Payload:</p>
                  <button
                    type="button"
                    onClick={handleCopyToClipboard}
                    className="flex items-center space-x-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded transition-colors"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3 h-3" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy JSON</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isTesting}
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleTest}
                disabled={isTesting || !eventId.trim() || !caseId.trim() || !claimDataJson.trim()}
              >
                {isTesting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Test Mapper
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TestMapperButton;
